/* eslint-disable max-statements, complexity */
import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import PostInspectionDiyTabCard from "../post-inspection-diy-tab-card";
import { BANNER_CHOICES, PICKUP_NOT_ACTIVE, POPUP_TYPES, STEP_NAMES, TAB_STATUS } from "./constant";
import styles from "./styles.css";
import loadable from "@loadable/component";
import PostInspectionDiyBankAccount from "../post-inspection-diy-bank-account";
import { getBankStatus, getPickUpStatus, isTokenEligible, getDocsStatus, bankAndEkycVerification, callGaEventforStep, isBankVerificationCompleted, getDocumentationSteps, getLoanDocsStatus, BANK_UNDER_VERIFICATION_STATES } from "./selector";
import PostInspectionDiySchedulePickup from "../post-inspection-diy-schedule-pickup";
import { PRICE_DISCOVERY } from "../../../constants/seller-diy-constants";
import PostInspectionDiyDocUpload from "../post-inspection-diy-doc-upload";
import PostInspectionDiyTokenContainer from "../post-inspection-diy-token-container";
import { NUMBER } from "../../../constants/app-constants";
import PostInspectionDiyTabPopUp from "../post-inspection-diy-tab-popup";
import isObjectEmpty from "../../../utils/helpers/isObjectEmpty";
import PostInspectionDiyVideoStepsBanner from "../post-inspection-diy-video-steps-banner";
import PostInspectionDiyLoanDetails from "../post-inspection-diy-loan-details";
import { REQUIRED_DOCUMENTS_LIST } from "../post-inspection-diy/constant";
import { LOAN_STATUS } from "../post-inspection-diy-loan-details/constant";
import Skeleton from "./skeleton";
import { TAB_NAMES, URL_APPLICABLE_PARAMS_LOAN } from "../post-inspection-diy-header-v2/constant";
import { FRANCHISE_BOUGHT_STATUS } from "../post-inspection-franchise-price-tab/constant";
import { BodyText } from "@cars24/turbo-web/lib/body-text";
import Light from "./images/light.svg";

const PostInspectionFranchiseDealerDetails = loadable(() => import("../post-inspection-franchise-dealer-details"));
const PostInspectionDiyLeadmonBanner = loadable(() => import("../post-inspection-diy-leadmon-banner"));
const PostInspectionDiyUserDetails = loadable(() => import("../post-inspection-diy-user-details"));

// eslint-disable-next-line max-statements
const PostInspectionDiyDocumentationTab = ({
    activeTab,
    postTokenDocuments,
    inspectionResponse,
    setActiveTabConnect,
    diyPaymentApprovalProcessConnect,
    getPurchaseRequestDetailsConnect,
    getDsiEligibilityConnect,
    auctionResponse,
    pickupDetail,
    helperVideos = [],
    screenParam = "",
    setScreenParam = () => {},
    setPageLoadingConnect = () => {},
    fetchLoanDetailsConnect = () => {},
    continueLoading = false,
    showBookSlotHandler = () => { },
    showSelfBought = false,
    franchiseDetails = {},
    bidStatus
}) => {
    const [screen, setScreen] = useState(null);
    const [showTokenEligibleContainer, setShowTokenEligibleContainer] = useState(false);
    const [popupContainerType, setPopupContainerType] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const { bank: bankDetails } = postTokenDocuments || {};
    const { transactionStatus = "", verified } = bankDetails || {};
    const { token, pickupStatus, priceDiscovery, appointmentId, isHypothecation, isDeliveryApproved } = inspectionResponse || {};
    const { slotExpired = false } = pickupDetail || {};
    const { offeredPrice = NUMBER.ZERO, isExpired = false } = auctionResponse || {};
    const isPriceAccepted = priceDiscovery === PRICE_DISCOVERY.ACCEPTED;
    const { isMismatchAccepted, dealerDetails } = franchiseDetails || {};
    const {dealerShipName = ""} = dealerDetails || {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const steps = useMemo(() => getDocumentationSteps(isHypothecation, showSelfBought), []);

    useEffect(() => {
        if (activeTab === TAB_NAMES.DOCUMENTATION) {
            setScreen(null);
        }
    }, [activeTab]);

    const setActiveTabScreen = (name) => {
        setActiveTabConnect(name);
        setScreen(name);
    };

    const pickUpHandling = (name) => {
        if (offeredPrice <= NUMBER.ZERO || isExpired) return;
        else if (slotExpired) setActiveTabScreen(name);
        else if (!isPriceAccepted) setPopupContainerType(POPUP_TYPES.SLOT_UNAVAILABLE);
        else if (isDeliveryApproved) showBookSlotHandler();
        else if (!PICKUP_NOT_ACTIVE.includes(pickupStatus)) setPopupContainerType(POPUP_TYPES.POST_SCHEDULE);
        else setActiveTabScreen(name);
    };

    const onClick = (name) => {
        callGaEventforStep(name, appointmentId);
        if (name === STEP_NAMES.BANK && isBankVerificationCompleted({bankDetails, isDeliveryApproved, showSelfBought, isMismatchAccepted})) setPopupContainerType(POPUP_TYPES.BANK_ACCOUNT);
        else if (name === STEP_NAMES.PICKUP) pickUpHandling(name);
        else if (name === STEP_NAMES.DOCS && getDocsStatus(postTokenDocuments[REQUIRED_DOCUMENTS_LIST.KYC_DOCUMENTS], isDeliveryApproved) === TAB_STATUS.VERIFIED) setPopupContainerType(POPUP_TYPES.DOCUMENTS);
        else setActiveTabScreen(name);
    };

    const renderLayout = () => {
        if (screen === STEP_NAMES.BANK) return <PostInspectionDiyBankAccount />;
        if (screen === STEP_NAMES.DOCS) return <PostInspectionDiyDocUpload />;
        if (screen === STEP_NAMES.PICKUP) return <PostInspectionDiySchedulePickup />;
        if (screen === STEP_NAMES.LOAN) return <PostInspectionDiyLoanDetails />;
        return null;
    };

    const getStatus = (name) => {
        if (name === STEP_NAMES.BANK) return getBankStatus(bankDetails, isDeliveryApproved, showSelfBought, isMismatchAccepted);
        if (name === STEP_NAMES.DOCS) return getDocsStatus(postTokenDocuments[REQUIRED_DOCUMENTS_LIST.KYC_DOCUMENTS], isDeliveryApproved, showSelfBought, isMismatchAccepted);
        if (name === STEP_NAMES.PICKUP) return getPickUpStatus({pickupStatus, isPriceAccepted, slotExpired, isDeliveryApproved});
        if (name === STEP_NAMES.LOAN) return getLoanDocsStatus(postTokenDocuments[REQUIRED_DOCUMENTS_LIST.LOAN_DOCUMENTS], isDeliveryApproved);
        return "";
    };

    const setPopupContainerHandler = () => {
        setPopupContainerType(null);
        window.scrollTo(0, 0);
    };

    const getPrDetails = (paymentApprovalRes = {}) => {
        getPurchaseRequestDetailsConnect(token).then((purchaseRequestResp) => {
            const { purchaseRequest = {} } = purchaseRequestResp;
            const { diyIntent = "" } = purchaseRequest;
            if (URL_APPLICABLE_PARAMS_LOAN.includes(screenParam) && !showSelfBought) {
                if (isHypothecation) {
                    onClick(STEP_NAMES.LOAN);
                }
                setScreenParam("");
                setPageLoadingConnect(false);
            }
            if (!isObjectEmpty(paymentApprovalRes) && !diyIntent && !showSelfBought) {
                const isBankAndEkycVerified = bankAndEkycVerification(paymentApprovalRes);
                if (offeredPrice > NUMBER.ZERO  && isBankAndEkycVerified && !isExpired) {
                    getDsiEligibilityConnect(token).then((response) => {
                        if (isTokenEligible(response.dsiEligibility)) {
                            setShowTokenEligibleContainer(true);
                        }
                    }).catch(() => {});
                }
            } else if (diyIntent) {
                setShowTokenEligibleContainer(true);
            }
            if (continueLoading) setPageLoadingConnect(false);
        }).catch(() => {
            // setIsLoading(false);
            if (URL_APPLICABLE_PARAMS_LOAN.includes(screenParam))   setPageLoadingConnect(false);
        });
    };

    const fetchData = async () => {
        setIsLoading(true);
        try {
            let loanStatus = null;
            if (isHypothecation && !showSelfBought) {
                const loanDetails = await fetchLoanDetailsConnect(token);
                loanStatus = loanDetails.loanStatus;
            }
            const res = await diyPaymentApprovalProcessConnect(token, loanStatus ? loanStatus : LOAN_STATUS.PENDING);
            getPrDetails(res);
        } catch (error) {
            getPrDetails();
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isKYCVerified = () => getDocsStatus(postTokenDocuments[REQUIRED_DOCUMENTS_LIST.KYC_DOCUMENTS]) === TAB_STATUS.VERIFIED;

    const isMisMatchFranchise = () =>
        BANK_UNDER_VERIFICATION_STATES.includes(transactionStatus) &&
    showSelfBought &&
    isMismatchAccepted === false;

    const getLuxuryCarMessage = () => {
        if (isMismatchAccepted && isKYCVerified()) return "Yay! All your documents have been verified";
        if (isMismatchAccepted) {
            if (!isKYCVerified()) return "Please upload pending documents to close the deal";
            return `${steps ? steps.length : "2"} simple steps to sell your car!`;
        }
        if (isMisMatchFranchise()) return "Your verification request was rejected because of details mismatch!";
        return `We are awaiting confirmation from ${dealerShipName}`;
    };

    const getLuxuryCarMessageBankVerified = () => {
        if (verified && isKYCVerified()) return "Yay! All your documents have been verified";
        if (verified && !isKYCVerified()) return "Please upload pending documents to close the deal";
        if (bidStatus === FRANCHISE_BOUGHT_STATUS.PRICE_CONSENT_ACCEPTED) return "You are 2 steps away from selling your car! 🚗";
        return `${steps ? steps.length : "3"} simple steps to sell your car!`;
    };

    const getHeaderDetails = () => {
        if (BANK_UNDER_VERIFICATION_STATES.includes(transactionStatus) && showSelfBought) {
            return getLuxuryCarMessage();
        } else if (showSelfBought) {
            return getLuxuryCarMessageBankVerified();
        }
        return `${steps ? steps.length : "3"} simple steps to sell your car!`;
    };

    if (isLoading) return <Skeleton/>;

    if (!screen) {
        return (
            <React.Fragment>
                <div styleName={"styles.stepsWrapper"}>
                    <h2>{getHeaderDetails()}</h2>
                    {isMisMatchFranchise() && <div styleName={"styles.info"}>
                        <img src={Light} alt="light"/>
                        <BodyText level={NUMBER.THREE}>Please contact your representative and add bank account details again</BodyText>
                    </div>}
                    {
                        steps.map(({title, icon, name, subTitle}, index) => (
                            <PostInspectionDiyTabCard
                                title={title}
                                subTitle={subTitle}
                                key={index}
                                name={name}
                                onClick={onClick}
                                status={getStatus(name)}
                            >
                                <img src={icon} />
                            </PostInspectionDiyTabCard>
                        ))
                    }
                    {helperVideos && !!helperVideos.length && <PostInspectionDiyVideoStepsBanner />}
                    {showSelfBought && <PostInspectionFranchiseDealerDetails />}
                    {showSelfBought && <PostInspectionDiyLeadmonBanner
                        heading="About premium franchise partners"
                        bannerDetails={BANNER_CHOICES}
                    />}
                    {!showSelfBought && <PostInspectionDiyUserDetails isRevamped={false} />}
                </div>
                {showTokenEligibleContainer &&
                    <PostInspectionDiyTokenContainer
                        setShowTokenEligibleContainer={setShowTokenEligibleContainer}
                    />
                }
                {popupContainerType &&
                    <PostInspectionDiyTabPopUp
                        popupContainerType= {popupContainerType}
                        setPopupContainerType= {setPopupContainerHandler}
                    />
                }
            </React.Fragment>
        );
    }
    return renderLayout();
};

PostInspectionDiyDocumentationTab.propTypes = {
    activeTab: PropTypes.string,
    postTokenDocuments: PropTypes.object,
    inspectionResponse: PropTypes.object,
    setActiveTabConnect: PropTypes.func,
    diyPaymentApprovalProcessConnect: PropTypes.func,
    getPurchaseRequestDetailsConnect: PropTypes.func,
    getDsiEligibilityConnect: PropTypes.func,
    auctionResponse: PropTypes.object,
    pickupDetail: PropTypes.object,
    helperVideos: PropTypes.array,
    screenParam: PropTypes.string,
    setScreenParam: PropTypes.func,
    setPageLoadingConnect: PropTypes.func,
    fetchLoanDetailsConnect: PropTypes.func,
    continueLoading: PropTypes.bool,
    showBookSlotHandler: PropTypes.func,
    showSelfBought: PropTypes.bool,
    franchiseDetails: PropTypes.object,
    bidStatus: PropTypes.string
};

export default PostInspectionDiyDocumentationTab;
